import { useContext, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box } from '@zillow/constellation-10';
import { LoadingButton } from '@zg-rentals/loading-components';
import { useClientAppConfigWebHandler_getFeature } from '@zg-rentals/rental-manager-env';

import { FreemiumContext } from '../Freemium/context';
import { RMWContext } from '../../providers/RMWContext';
import { LoadingMapContext } from '../../providers/LoadingMapContext/context';

import { useSelector } from '../../selectors/useSelector';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';
import { VERTICAL_DISPLAY_TYPE } from '../../constants/PropertiesConstants';
import { TENANT_PLACEMENT_COOKIES } from '../../constants/CookieConstants';
import { screenSize as SCREEN_SIZE } from '../../utils/browserUtils';

import accessUtils from '../../utils/accessUtils';

import ConnectedModuleCard from '../../components/ConnectedModuleCard';
import ListingAvailability from '../ConnectedListing/components/ListingAvailability';
import PremiumCTA from '../ConnectedListing/components/PremiumCTA';

import Details from '../ConnectedListing/components/Details';
import TenantPlacementUpsellModal from '../Modal/TenantPlacementUpsellModal';

import ConnectedNetworkView, { DISPLAY_TYPE } from '../ConnectedNetworkView';
import TenantPlacementUpsellBanner from '../TenantPlacementUpsellBanner';

import { Text } from '@zillow/constellation-10';
import { singleTextLineStyles } from '../../styles';

import { ListingModuleDetailsCta } from './components/ListingModuleDetailsCta';
import { ListingModuleHeading } from './components/ListingModuleHeading';
import WhenListingExpireLearnMore from './components/WhenListingExpireLearnMore';

import type { MouseEvent } from 'react';

const ListingModule = () => {
  const [showTenantPlacementModal, setShowTenantPlacementModal] = useState(false);

  const {
    freemiumState: { canAccessTopOfFunnelPremium },
  } = useContext(FreemiumContext);

  const { paths } = useContext(RMWContext);

  const {
    loadingByComponent: { ListingModule: isListingModuleLoading },
  } = useContext(LoadingMapContext);

  const {
    cookieEnabledServices,
    user,
    currentProperty,
    active,
    activeProductType,
    expirationTimestamp: activeProductExpirationTimestamp,
    constellationScreenSize,
    expirationDateTimestamp,
    isFreemiumEnabled,
    listingAlias,
    rentZestimate,
    shouldCheckIn,
    status,
    syndication,
  } = useSelector((state) => {
    return {
      cookieEnabledServices: state.app.cookieEnabledServices,
      user: state.user,
      currentProperty: state.properties.currentProperty,
      active: state.properties.currentProperty.active,
      activeProductType: state.properties.currentProperty.freemium.activeProduct.type,
      expirationTimestamp: state.properties.currentProperty.freemium.activeProduct.expirationTimestamp,
      constellationScreenSize: state.app.constellationScreenSize,
      expirationDateTimestamp: state.properties.currentProperty.expirationDateTimestamp,
      isFreemiumEnabled: state.properties.currentProperty.freemium.isEnabled,
      listingAlias: state.properties.currentProperty.listingAlias,
      rentZestimate: state.properties.currentProperty.rentZestimate,
      shouldCheckIn: state.properties.propertyOverview.listingAvailability.shouldCheckIn,
      status: state?.properties?.currentProperty?.syndication?.overview?.status,
      syndication: state.properties.currentProperty.syndication,
    };
  }, shallowEqual);

  const { data: enabledStates = '' } = useClientAppConfigWebHandler_getFeature({
    appName: 'zrm_web',
    featureName: 'tenantPlacementStates',
  });

  const canAccessModularListing = accessUtils.canAccessModularListing({
    currentProperty,
    cookieEnabledServices,
    user,
  });

  const canAccessTenantPlacementDashboardModal = accessUtils.canAccessTenantPlacement({
    enabledStates,
    currentProperty,
    user,
    cookie: TENANT_PLACEMENT_COOKIES.HAS_CLICKED_DASHBOARD_POST,
  });

  const canAccessTenantPlacementListingModuleBanner = accessUtils.canAccessTenantPlacementListingModuleBanner({
    currentProperty,
    user,
    enabledStates,
  });

  const listingPath = `${paths.propertiesPage}/${listingAlias}/${
    canAccessModularListing ? 'create-listing' : 'listing'
  }`;
  const shouldDisplayListingAvailability = Boolean(expirationDateTimestamp && shouldCheckIn);

  const isActiveProductTypePremium = activeProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;
  const isActiveProductTypeFree = activeProductType === PRODUCT_TYPE_NAME_TO_ID.FREE;

  // keep same logic as in ConnectedListing
  const displayPremiumCTA = (isFreemiumEnabled && active) || (canAccessTopOfFunnelPremium && !isActiveProductTypeFree);

  const activeCTA = 'Edit your listing';
  const inactiveCTA = 'List your property';

  const handleDetailsClick = (event: MouseEvent<HTMLAnchorElement>) => {
    handleGoToListingClick(event);
  };

  const handleGoToListingClick = (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    event.preventDefault();
    if (canAccessTenantPlacementDashboardModal && !currentProperty.dateLastActivatedTimestamp && !active) {
      setShowTenantPlacementModal(true);
    } else {
      transitionToListingPath();
    }
  };

  const transitionToListingPath = () => {
    window.router.transitionTo(listingPath, '_self');
  };

  const getListingTypeModuleCardHeading = () => {
    if (isFreemiumEnabled && isActiveProductTypePremium) {
      return 'Premium listing';
    }

    if (isFreemiumEnabled && active && isActiveProductTypeFree) {
      return 'Basic listing';
    }

    return 'Listing';
  };

  return (
    <ConnectedModuleCard
      data-testid="ListingModule"
      leftComponent={
        <ListingModuleHeading
          isPremiumListing={isFreemiumEnabled && isActiveProductTypePremium}
          headingText={getListingTypeModuleCardHeading()}
        />
      }
      ctaComponent={<ListingModuleDetailsCta listingPath={listingPath} onClick={handleDetailsClick} />}
      ctaFontColor="text.subtle"
      ctaFontType="body-sm"
      justifyHeadingContent={'space-between'}
      isLoading={false}
      includeBorderTop={isFreemiumEnabled && isActiveProductTypePremium}
    >
      {shouldDisplayListingAvailability && (
        <Box css={{ marginBottom: '800' }}>
          <ListingAvailability />
        </Box>
      )}

      <Details displayType={VERTICAL_DISPLAY_TYPE.SIMPLE} rentZestimate={rentZestimate} />

      {Boolean(active && !shouldDisplayListingAvailability) && <WhenListingExpireLearnMore />}

      {displayPremiumCTA && isActiveProductTypePremium && (
        <PremiumCTA
          isActiveProductTypeFree={isActiveProductTypeFree}
          activeProductExpirationTimestamp={activeProductExpirationTimestamp}
          isFromListingModule={true}
        />
      )}

      <Box css={{ marginBottom: { base: '400', lg: '600' } }} />

      {active ? (
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns:
              constellationScreenSize === SCREEN_SIZE.XS || constellationScreenSize === SCREEN_SIZE.SM
                ? 'repeat(1, minmax(0,1fr))'
                : 'repeat(auto-fit, minmax(0,1fr))',
            gap: '200',
          }}
        >
          <Box>
            <ConnectedNetworkView
              displayType={DISPLAY_TYPE.DROPDOWN_BUTTON}
              label="View your listing"
              size="md"
              status={status}
              syndication={syndication}
              isLoading={isListingModuleLoading}
            />
          </Box>

          <Box>
            <LoadingButton
              tone="neutral"
              emphasis="outlined"
              fluid={true}
              onClick={handleGoToListingClick}
              title={activeCTA}
              isLoading={isListingModuleLoading}
              loadingWidth="100%"
            >
              <Text textStyle="body-bold" css={singleTextLineStyles}>
                {activeCTA}
              </Text>
            </LoadingButton>
          </Box>
        </Box>
      ) : (
        <LoadingButton
          fluid={true}
          tone="neutral"
          emphasis="outlined"
          onClick={handleGoToListingClick}
          title={inactiveCTA}
          isLoading={isListingModuleLoading}
          loadingWidth="100%"
        >
          <Text textStyle="body-bold" css={singleTextLineStyles}>
            {inactiveCTA}
          </Text>
        </LoadingButton>
      )}

      {canAccessTenantPlacementListingModuleBanner && (
        <Box css={{ marginTop: { base: '400', lg: '600' } }}>
          <TenantPlacementUpsellBanner />
        </Box>
      )}

      {showTenantPlacementModal && (
        <TenantPlacementUpsellModal
          onClose={() => setShowTenantPlacementModal(false)}
          onPostListingClick={transitionToListingPath}
        />
      )}
    </ConnectedModuleCard>
  );
};

export default ListingModule;
